import React, { useEffect, useRef } from 'react';

import type { PreloadedEntryPoint } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ContentReadySignal } from '../use-content-ready-signal';
import { WaitForEntryPointLoad } from '../wait-for-entry-point-load';

export type FocusOnLoadProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	onLoad: (container: HTMLElement) => void;
	waitForContentReady: ContentReadySignal['waitForContentReady'];
};

function FocusOnLoadInner({
	onLoad,
	waitForContentReady,
}: Pick<FocusOnLoadProps, 'onLoad' | 'waitForContentReady'>): React.JSX.Element {
	const anchorRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		// `Popup` does not expose a ref to the element wrapping its content, so
		// it is resolved through this anchor, which renders as a sibling of the
		// loaded experience inside that element.
		const container = anchorRef.current?.parentElement;

		if (!container) return;

		const focusContent = () => onLoad(container);

		if (!fg('platform_dst-trigger-focus-after-content')) {
			focusContent();
			return;
		}

		return waitForContentReady(focusContent);
	}, [onLoad, waitForContentReady]);

	return <span ref={anchorRef} hidden />;
}

export function FocusOnLoad({
	entryPointReference,
	onLoad,
	waitForContentReady,
}: FocusOnLoadProps): React.JSX.Element {
	return (
		<WaitForEntryPointLoad entryPointReference={entryPointReference}>
			<FocusOnLoadInner onLoad={onLoad} waitForContentReady={waitForContentReady} />
		</WaitForEntryPointLoad>
	);
}
